import { useState } from 'react'
import { NavLink, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { apiGet, apiPost } from '../lib/api'
import { 
  Award, 
  BookOpen, 
  Bell, 
  User, 
  Menu, 
  X, 
  LogOut,
  ArrowLeft,
  ExternalLink
} from 'lucide-react'

interface UserLayoutProps {
  children: React.ReactNode
}

interface Announcement {
  id: string
  title: string
  message: string
  linkUrl: string | null
  linkText: string | null
  displayMode: string
  isActive: boolean
}

const navigation = [
  { name: 'Certifications', href: '/app', icon: Award },
  { name: 'Continuing Education', href: '/ce', icon: BookOpen },
  { name: 'Notifications', href: '/notifications', icon: Bell },
  { name: 'Profile', href: '/profile', icon: User },
]

export default function UserLayout({ children }: UserLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, logout, isLoggingOut } = useAuth()
  
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  const { data: announcementsData } = useQuery<{ announcements: Announcement[] }>({
    queryKey: ['/api/announcements'],
    queryFn: () => apiGet<{ announcements: Announcement[] }>('/announcements'),
    enabled: !!user,
  })

  const dismissMutation = useMutation({
    mutationFn: (announcementId: string) => apiPost(`/announcements/${announcementId}/dismiss`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/announcements'] })
    },
  })

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const activeAnnouncements = announcementsData?.announcements || []

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-900/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Award className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">FreedomCert</h1>
              <p className="text-xs text-gray-500">Certification Tracker</p>
            </div>
            <button 
              className="ml-auto lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Admin Dashboard Link */}
          {isAdmin && (
            <Link
              to="/admin"
              onClick={() => setSidebarOpen(false)}
              className="mx-3 mt-4 flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Admin Dashboard
            </Link>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/app'}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
                  ${isActive 
                    ? 'bg-blue-50 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="border-t border-gray-100 p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-700 font-semibold text-sm">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              {isLoggingOut ? 'Signing out...' : 'Sign out'}
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 lg:hidden">
          <div className="flex items-center gap-4 px-4 py-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Award className="w-6 h-6 text-blue-600" />
              <span className="font-semibold text-gray-900">FreedomCert</span>
            </div>
          </div>
        </header>

        {/* Announcement Banners */}
        {activeAnnouncements.length > 0 && (
          <div className="space-y-0">
            {activeAnnouncements.map((announcement) => (
              <div
                key={announcement.id}
                className="bg-blue-600 text-white px-4 py-3 flex items-start justify-between gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">{announcement.title}</p>
                  <p className="text-sm text-blue-100 mt-0.5">{announcement.message}</p>
                  {announcement.linkUrl && announcement.linkText && (
                    <a
                      href={announcement.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm font-medium text-white underline mt-1 hover:text-blue-200"
                    >
                      {announcement.linkText}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); dismissMutation.mutate(announcement.id); }}
                  className="flex-shrink-0 p-2 hover:bg-blue-700 rounded transition-colors cursor-pointer relative z-10"
                  aria-label="Dismiss"
                  type="button"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Page content */}
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  )
}
